import { useEffect, useState } from 'react';
import {
  Camera,
  ShieldCheck,
  Battery,
  Signal,
  Trash2,
  X,
  ExternalLink,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Radio,
  Image as ImageIcon,
  Clock,
  Compass,
} from 'lucide-react';
import type { Field, Estate } from '../../types';
import {
  formatGpsCoords,
  getFieldCentroid,
  formatJagtPoiNotes,
  type JagtPoiConfig,
  type JagtPoiData,
} from '../../lib/poi';
import {
  loadEstateIntegrations,
  unlinkFieldFromIntegrations,
  type TrailCameraIntegration,
  type TrapSensorIntegration,
} from '../../lib/integrations';
import { A11yDialog } from '../A11yDialog';

interface Props {
  field: Field;
  poi: JagtPoiData;
  poiConfig: JagtPoiConfig;
  estate: Estate;
  planReadOnly: boolean;
  updateField: (id: string, patch: Partial<Field>) => void;
  deleteField: (field: Field) => void;
  onClose: () => void;
  onNavigateToIntegrations: (tab: 'cameras' | 'traps') => void;
  notify?: (msg: string) => void;
}

export function PoiIntegrationInspector({
  field,
  poi,
  poiConfig,
  estate,
  planReadOnly,
  updateField,
  deleteField,
  onClose,
  onNavigateToIntegrations,
  notify,
}: Props) {
  const isCamera = poi.type === 'vildtkamera';
  const isTrap = poi.type === 'faeldesensor';

  const [integrations, setIntegrations] = useState(() => loadEstateIntegrations(estate.id));
  const [selectedPhotoModal, setSelectedPhotoModal] = useState<string | null>(null);

  useEffect(() => {
    setIntegrations(loadEstateIntegrations(estate.id));
  }, [estate.id, field.id]);

  const centroid = getFieldCentroid(field.geometry);
  const lat = centroid?.lat ?? 55.4;
  const lng = centroid?.lng ?? 11.8;

  // Find tilknyttet kamera eller fælde
  const linkedCamera: TrailCameraIntegration | undefined = isCamera
    ? integrations.cameras.find(
        (c) =>
          c.fieldId === field.id || c.name.trim().toLowerCase() === field.name.trim().toLowerCase(),
      ) || integrations.cameras[0]
    : undefined;

  const linkedTrap: TrapSensorIntegration | undefined = isTrap
    ? integrations.traps.find(
        (t) =>
          t.fieldId === field.id || t.name.trim().toLowerCase() === field.name.trim().toLowerCase(),
      ) || integrations.traps[0]
    : undefined;

  const handleDelete = () => {
    // Slet markøren fra kortet og ryd koblingen i integrationer
    unlinkFieldFromIntegrations(estate.id, field.id);
    deleteField(field);
    if (notify) {
      notify(
        isCamera
          ? `Kamera '${field.name}' er fjernet fra kortet.`
          : `Fældesensor '${field.name}' er fjernet fra kortet.`,
      );
    }
    onClose();
  };

  return (
    <div className="field-inspector is-poi-inspector">
      <div className="sheet-handle" aria-hidden="true" />

      {/* Header */}
      <div className="section-heading">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isCamera ? (
            <Camera size={20} style={{ color: '#1b3b22' }} />
          ) : (
            <ShieldCheck size={20} style={{ color: '#15803d' }} />
          )}
          <span>{field.name}</span>
        </h3>
        <button
          className="icon-button"
          aria-label="Luk oplysninger"
          onClick={onClose}
          type="button"
        >
          <X size={17} />
        </button>
      </div>

      {/* Telemetri & Status Kort */}
      {isCamera && linkedCamera && (
        <div
          style={{
            background: '#f8faf6',
            border: '1px solid #dce8d8',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: linkedCamera.status === 'online' ? '#15803d' : '#6b7280',
                background: linkedCamera.status === 'online' ? '#dcfce7' : '#f3f4f6',
                padding: '3px 8px',
                borderRadius: 12,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: linkedCamera.status === 'online' ? '#22c55e' : '#9ca3af',
                }}
              />
              {linkedCamera.brand.toUpperCase()} 4G ONLINE
            </span>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803d' }}
              >
                <Battery size={14} />
                {linkedCamera.batteryPercent}%
              </span>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0369a1' }}
              >
                <Signal size={14} />
                {linkedCamera.signalLevel}/5
              </span>
            </div>
          </div>

          <div style={{ fontSize: 12, color: '#4c5f49', marginBottom: 8 }}>
            Model: <strong>{linkedCamera.modelName || 'UCon 4G LTE Solcelle'}</strong>
          </div>

          {/* Seneste Foto Miniature */}
          {linkedCamera.lastPhotoUrl && (
            <div
              style={{
                position: 'relative',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #c8d9c4',
                marginBottom: 10,
                cursor: 'pointer',
              }}
              onClick={() => setSelectedPhotoModal(linkedCamera.lastPhotoUrl || null)}
              title="Klik for at se i stor visning"
            >
              <img
                src={linkedCamera.lastPhotoUrl}
                alt={linkedCamera.name}
                style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                  padding: '6px 8px',
                  color: '#fff',
                  fontSize: 11,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <span>{linkedCamera.notes || 'Observation'}</span>
                <span style={{ opacity: 0.85, fontSize: 10 }}>🔍 Klik for stor</span>
              </div>
            </div>
          )}

          {false && (
            <button
              type="button"
              className="button secondary"
              style={{
                width: '100%',
                minHeight: 38,
                fontSize: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
              onClick={() => onNavigateToIntegrations('cameras')}
            >
              <ImageIcon size={14} />
              <span>
                Se fotogalleri ({linkedCamera?.photos?.length || 1} fotos) i Integrationer
              </span>
              <ExternalLink size={12} />
            </button>
          )}
        </div>
      )}

      {isTrap && linkedTrap && (
        <div
          style={{
            background: '#f8faf6',
            border: '1px solid #dce8d8',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#15803d',
                background: '#dcfce7',
                padding: '3px 8px',
                borderRadius: 12,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#22c55e',
                }}
              />
              {linkedTrap.brand.toUpperCase()} TILSYN OK
            </span>

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: '#15803d',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Battery size={14} />
              {linkedTrap.batteryPercent}%
            </span>
          </div>

          <div style={{ fontSize: 12, color: '#4c5f49', marginBottom: 6 }}>
            Sidste rapport:{' '}
            <strong>
              {new Date(linkedTrap.lastHeartbeatAt).toLocaleTimeString('da-DK', {
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              (I dag)
            </strong>
          </div>

          {false && (
            <button
              type="button"
              className="button secondary"
              style={{
                width: '100%',
                minHeight: 38,
                fontSize: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
              onClick={() => onNavigateToIntegrations('traps')}
            >
              <ShieldCheck size={14} />
              <span>Åbn fældesensorer i Integrationer</span>
              <ExternalLink size={12} />
            </button>
          )}
        </div>
      )}

      {/* GPS Placering */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: '#52664f',
          background: '#f2f7ef',
          padding: '8px 10px',
          borderRadius: 8,
          marginBottom: 12,
        }}
      >
        <MapPin size={13} style={{ color: '#1b3b22', flexShrink: 0 }} />
        <span>GPS: {formatGpsCoords(lat, lng)} · Træk i punktet for at flytte placering</span>
      </div>

      {/* Redigering af Navn */}
      <label className="field-label">
        Navn
        <input
          className="form-input"
          disabled={planReadOnly}
          value={field.name}
          onChange={(e) => updateField(field.id, { name: e.target.value })}
          onBlur={() => {
            if (!field.name.trim()) {
              updateField(field.id, { name: poiConfig.defaultName });
            }
          }}
        />
      </label>

      {/* Redigering af Noter */}
      <label className="field-label" style={{ marginTop: 12 }}>
        Noter &amp; Standplads
        <textarea
          className="form-input"
          disabled={planReadOnly}
          rows={2}
          value={poi.userNotes}
          onChange={(e) => {
            const currentDirs = poi.directions || (poi.direction ? [poi.direction] : []);
            const newNotes = formatJagtPoiNotes(poi.type, currentDirs, e.target.value);
            updateField(field.id, { notes: newNotes });
          }}
          placeholder="Terræn, stand, placering, dækning…"
        />
      </label>

      {/* Handlingsknapper */}
      <div className="inspector-foot" style={{ marginTop: 18 }}>
        <button
          type="button"
          className="text-button danger"
          disabled={planReadOnly}
          onClick={handleDelete}
        >
          <Trash2 size={14} />
          Slet {isCamera ? 'vildtkamera' : 'fældesensor'} fra kortet
        </button>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="button primary"
          style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
          onClick={onClose}
        >
          Luk
        </button>
      </div>

      {/* Fuldskærms Foto Modal */}
      {selectedPhotoModal && (
        <A11yDialog
          aria-label="Fuldskærmsbillede"
          onClose={() => setSelectedPhotoModal(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 'calc(var(--z-popover) + 9)',
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setSelectedPhotoModal(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: 800,
              maxHeight: '90dvh',
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedPhotoModal(null)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.6)',
                border: 'none',
                color: '#fff',
                width: 36,
                height: 36,
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>
            <img
              src={selectedPhotoModal}
              alt="Forstørret vildtfoto"
              style={{ width: '100%', maxHeight: '80dvh', objectFit: 'contain', display: 'block' }}
            />
            <div
              style={{ padding: '12px 16px', background: '#1c2e20', color: '#fff', fontSize: 13 }}
            >
              {field.name} · Observation
            </div>
          </div>
        </A11yDialog>
      )}
    </div>
  );
}
